import { Injector, WritableSignal, afterNextRender } from '@angular/core';
import { FieldTree } from '@angular/forms/signals';

/**
 * Repairs a Signal Form that was seeded before an `ngx-mask` input initialised
 * (SEOMS-477).
 *
 * ## What goes wrong
 *
 * `NgxMaskDirective` supports Signal Forms as a *custom control*: it exposes a
 * `value` model that `[formField]` binds to. On its first `ngOnChanges` pass it
 * calls `_applyMask()` — which masks its own still-empty internal value and
 * emits that empty result through `onChange` — **before** it replays the value
 * that arrived earlier (`_pendingInitialValue`). Two things follow:
 *
 * 1. **The seeded value is lost.** The empty emission is written into the
 *    `value` model, and Signal Forms writes it into the data model. The replay
 *    then renders the real number into the DOM, but `writeValue` is a one-way
 *    model→view sync and never pushes it back — so the input *displays* the
 *    phone number while the model holds `''`. Saving from that state persists a
 *    blank.
 * 2. **The field is marked dirty.** Angular wraps a custom control's value
 *    signal so that every write calls `markAsDirty()` (there is no
 *    "programmatic write" escape hatch). ngx-mask does try to undo this via
 *    `_restoreControlStateAfterWrite`, but that path resolves an `NgControl` —
 *    which Signal Forms never creates — so it returns early and does nothing.
 *
 * ## What this does
 *
 * Re-applies the seeded value once the mask has configured, then clears the
 * dirty/touched state those programmatic writes produced, so the form is
 * pristine and holds its real data. Re-applying the *whole* seeded value covers
 * any number of masked fields, including ones inside arrays (contacts renders
 * one per phone row).
 *
 * Re-applying the value and clearing the state happen in the SAME callback, in
 * that order. Both are synchronous, so the reset observes the dirt the re-apply
 * just produced and clears it. Deferring the reset to a later render instead
 * makes it land after the user's first real edit and silently wipe *their*
 * dirty flag — which is worse than the bug being fixed.
 *
 * Only genuine post-init edits survive this: the callback runs once, during
 * initial seeding, before the user can interact.
 *
 * Remove this when ngx-mask replays its pending initial value before the first
 * `_applyMask()`, or grows a Signal-Forms-aware pristine restore.
 */
export function reseedAfterMaskInit<T>(
  model: WritableSignal<T>,
  field: FieldTree<T>,
  seeded: T,
  injector: Injector,
): void {
  afterNextRender(
    () => {
      model.set(seeded);
      field().reset();
    },
    { injector },
  );
}
